import { useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { Check, Link2, RefreshCw, Trash2, QrCode } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createInviteLink, getInviteLink, revokeInviteLink } from '../../shared/api/invite'
import { Button } from '../../shared/components/Button'

interface QrInvitePanelProps {
  tripId: string
}

export function QrInvitePanel({ tripId }: QrInvitePanelProps) {
  const qc = useQueryClient()
  const [showQr, setShowQr] = useState(false)
  const [copied, setCopied] = useState(false)

  const { data: link, isLoading } = useQuery({
    queryKey: ['invite-link', tripId],
    queryFn: () => getInviteLink(tripId),
    staleTime: 60_000,
  })

  const generate = useMutation({
    mutationFn: () => createInviteLink(tripId, 'guest'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invite-link', tripId] }),
  })

  const revoke = useMutation({
    mutationFn: () => revokeInviteLink(tripId),
    onSuccess: () => {
      qc.setQueryData(['invite-link', tripId], null)
      setShowQr(false)
    },
  })

  const joinUrl = link
    ? `${window.location.origin}/join/${link.token}`
    : null

  async function copyLink() {
    if (!joinUrl) return
    await navigator.clipboard.writeText(joinUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (isLoading) return null

  return (
    <div className="mt-4 rounded-st-lg border border-st-line bg-st-surface p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <QrCode className="h-4 w-4 text-st-primary" />
          <h2 className="text-sm font-semibold text-st-text">QR invite link</h2>
        </div>
        <div className="flex items-center gap-2">
          {link ? (
            <>
              <Button variant="outline" onClick={() => setShowQr((v) => !v)}>
                {showQr ? 'Hide QR' : 'Show QR'}
              </Button>
              <button
                type="button"
                onClick={() => void copyLink()}
                className="inline-flex items-center gap-1 rounded-st-md border border-st-line px-3 py-1.5 text-xs font-medium text-st-text hover:bg-st-surface-2"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-st-primary" /> : <Link2 className="h-3.5 w-3.5" />}
                {copied ? 'Copied!' : 'Copy link'}
              </button>
              <button
                type="button"
                onClick={() => generate.mutate()}
                title="Regenerate link"
                className="rounded-st-sm p-1.5 text-st-muted hover:text-st-text"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => revoke.mutate()}
                title="Revoke link"
                className="rounded-st-sm p-1.5 text-st-danger hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          ) : (
            <Button variant="outline" onClick={() => generate.mutate()} disabled={generate.isPending}>
              Generate link
            </Button>
          )}
        </div>
      </div>

      {link && (
        <p className="mt-2 text-xs text-st-muted">
          Anyone with this link joins as <strong>{link.role}</strong>
          {link.max_uses > 0 ? ` (${link.max_uses} max uses)` : ''}.
        </p>
      )}

      {showQr && joinUrl && (
        <div className="mt-4 flex justify-center">
          <div className="rounded-st-lg bg-white p-4 shadow-sm">
            <QRCodeSVG value={joinUrl} size={200} />
          </div>
        </div>
      )}
    </div>
  )
}
